import React, { useState, useEffect } from 'react';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useUser } from '../context/UserContext';
import { getStudentsWithTierAccess, grantTierAccess, revokeTierAccess } from '../services/featureService';
import StudentTierModal from './StudentTierModal';
import './FeatureFlags.css';

// ─── Toggle switch ────────────────────────────────────────────
const Toggle = ({ enabled, onChange, disabled }) => (
  <button
    className={`ff-toggle ${enabled ? 'on' : 'off'}`}
    onClick={() => !disabled && onChange(!enabled)}
    aria-pressed={enabled}
    disabled={disabled}
  >
    <span className="ff-toggle-knob" />
    <span className="ff-toggle-label">{enabled ? 'On' : 'Off'}</span>
  </button>
);

// ─── Tier row (expandable sub-section under each activity) ────
const TierRow = ({ activityType, tier, label, range }) => {
  const { flags, toggleFlag } = useFeatureFlags();
  const { currentUser } = useUser();
  const flagKey = `${activityType}_words_tier${tier}_enabled`;
  const enabled = !!flags[flagKey];

  const [grantedStudents, setGrantedStudents] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    setLoadingStudents(true);
    getStudentsWithTierAccess(activityType, tier).then(({ data }) => {
      setGrantedStudents(data || new Set());
      setLoadingStudents(false);
    });
  }, [enabled, activityType, tier]);

  const handleGrantToggle = async (studentId, currentlyGranted) => {
    if (currentlyGranted) {
      await revokeTierAccess(studentId, activityType, tier);
      setGrantedStudents(prev => { const s = new Set(prev); s.delete(studentId); return s; });
    } else {
      await grantTierAccess(studentId, activityType, tier, currentUser?.id);
      setGrantedStudents(prev => new Set([...prev, studentId]));
    }
  };

  return (
    <div className={`ff-tier-row ${enabled ? 'tier-enabled' : ''}`}>
      <div className="ff-tier-header">
        <div className="ff-tier-info">
          <span className="ff-tier-badge">Tier {tier}</span>
          <div>
            <p className="ff-tier-label">{label}</p>
            <p className="ff-tier-range">{range}</p>
          </div>
        </div>
        <Toggle enabled={enabled} onChange={(val) => toggleFlag(flagKey, val)} />
      </div>

      {enabled && (
        <div className="ff-tier-students">
          {loadingStudents ? (
            <p className="ff-loading">Loading students…</p>
          ) : (
            <>
              <p className="ff-tier-students-note">
                {grantedStudents.size === 0
                  ? 'No students assigned yet.'
                  : `${grantedStudents.size} student${grantedStudents.size > 1 ? 's' : ''} have access to these words.`}
              </p>
              <button className="ff-assign-btn" onClick={() => setModalOpen(true)}>
                👥 Manage Student Access
              </button>
            </>
          )}
        </div>
      )}

      {modalOpen && (
        <StudentTierModal
          activityType={activityType}
          tier={tier}
          tierLabel={label}
          grantedStudents={grantedStudents}
          onToggle={handleGrantToggle}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Activity section ─────────────────────────────────────────
const ActivitySection = ({ icon, title, activityType }) => (
  <div className="ff-activity-section">
    <div className="ff-activity-header">
      <span className="ff-activity-icon">{icon}</span>
      <h3>{title}</h3>
    </div>
    <div className="ff-tier-list">
      <div className="ff-tier-baseline">
        <span className="ff-tier-badge baseline">Tier 1</span>
        <div>
          <p className="ff-tier-label">Words 1–25</p>
          <p className="ff-tier-range">Always enabled · Available to all students</p>
        </div>
        <span className="ff-always-on">Always On</span>
      </div>
      <TierRow activityType={activityType} tier={2} label="Words 26–500" range="Extended vocabulary" />
      <TierRow activityType={activityType} tier={3} label="Words 501+" range="Advanced vocabulary" />
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────
const FeatureFlags = () => {
  const { flags, loading, toggleFlag } = useFeatureFlags();

  if (loading) {
    return (
      <div className="ff-loading-page">
        <div className="ff-spinner" />
        <p>Loading features…</p>
      </div>
    );
  }

  return (
    <div className="feature-flags-page">
      <div className="ff-page-header">
        <h2>⚙️ Feature Settings</h2>
        <p>Control which features are active in the application. Changes take effect immediately.</p>
      </div>

      {/* ── General ── */}
      <section className="ff-section">
        <h3 className="ff-section-title">General</h3>

        <div className="ff-card">
          <div className="ff-card-row">
            <div className="ff-card-info">
              <span className="ff-card-icon">📝</span>
              <div>
                <p className="ff-card-title">User Sign-Up</p>
                <p className="ff-card-desc">
                  {flags.signup_enabled
                    ? 'Sign-up link is visible. New students can register themselves.'
                    : 'Sign-up is hidden. Only admin can create student accounts.'}
                </p>
              </div>
            </div>
            <Toggle
              enabled={!!flags.signup_enabled}
              onChange={(val) => toggleFlag('signup_enabled', val)}
            />
          </div>
        </div>
      </section>

      {/* ── Marketing ── */}
      <section className="ff-section">
        <h3 className="ff-section-title">Marketing</h3>

        <div className="ff-card">
          <div className="ff-card-row">
            <div className="ff-card-info">
              <span className="ff-card-icon">🎬</span>
              <div>
                <p className="ff-card-title">Demo Video on Home Page</p>
                <p className="ff-card-desc">
                  {flags.marketing_video_enabled
                    ? 'A "Watch the Demo" button is visible on the home page next to the login button.'
                    : 'Demo video button is hidden. Home page shows only the login button.'}
                </p>
              </div>
            </div>
            <Toggle
              enabled={!!flags.marketing_video_enabled}
              onChange={(val) => toggleFlag('marketing_video_enabled', val)}
            />
          </div>
        </div>
      </section>

      {/* ── Learning Activities ── */}
      <section className="ff-section">
        <h3 className="ff-section-title">Learning Activities</h3>
      
        <div className="ff-card">
          <div className="ff-card-row">
            <div className="ff-card-info">
              <span className="ff-card-icon">&#x1F9EE;</span>
              <div>
                <p className="ff-card-title">Math Concepts</p>
                <p className="ff-card-desc">
                  {flags.math_concepts_enabled
                    ? 'Math Concepts activity is visible to students. Teaches Concrete, Representational, and Abstract math.'
                    : 'Math Concepts is hidden from the student menu.'}
                </p>
              </div>
            </div>
            <Toggle
              enabled={!!flags.math_concepts_enabled}
              onChange={(val) => toggleFlag('math_concepts_enabled', val)}
            />
          </div>
        </div>

        <div className="ff-card">
          <div className="ff-card-row">
            <div className="ff-card-info">
              <span className="ff-card-icon">&#x1F9FC;</span>
              <div>
                <p className="ff-card-title">Daily Living Skills</p>
                <p className="ff-card-desc">
                  {flags.daily_living_enabled
                    ? 'Daily Living Skills activity is visible to students. Step-by-step life-skill scenarios like Washing Hands.'
                    : 'Daily Living Skills is hidden from the student menu.'}
                </p>
              </div>
            </div>
            <Toggle
              enabled={!!flags.daily_living_enabled}
              onChange={(val) => toggleFlag('daily_living_enabled', val)}
            />
          </div>
        </div>
      </section>
      
      {/* ── Activities ── */}
      <section className="ff-section">
        <h3 className="ff-section-title">Word Activities — Tiers</h3>
        <p className="ff-section-desc">
          Tier 1 (words 1–25) is always on for every student. Enable higher tiers globally, then assign individual students below.
        </p>

        <div className="ff-activities-grid">
          <ActivitySection icon="👁️" title="Sight Words"    activityType="sight"    />
          <ActivitySection icon="🔀" title="Scramble Words" activityType="scramble" />
          <ActivitySection icon="🏷️" title="Category Words" activityType="category" />
        </div>
      </section>
    </div>
  );
};

export default FeatureFlags;
